import React, { useState, useEffect } from 'react';
import { MessageSquare, Save, AlertCircle, Info, Users, Crown, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../../lib/api-client';
import { Badge } from '../ui/badge';

interface ChatSettings {
  requireBothMembersHavePlan: boolean;
  allChatsFree: boolean;
  freeChatLimit: number;
}

export default function AdminChatSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [requireBothMembersHavePlan, setRequireBothMembersHavePlan] = useState(false);
  const [allChatsFree, setAllChatsFree] = useState(false);
  const [freeChatLimit, setFreeChatLimit] = useState(0);
  
  const [originalSettings, setOriginalSettings] = useState<ChatSettings>({
    requireBothMembersHavePlan: false,
    allChatsFree: false,
    freeChatLimit: 0
  });

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    setHasChanges(
      requireBothMembersHavePlan !== originalSettings.requireBothMembersHavePlan ||
      allChatsFree !== originalSettings.allChatsFree ||
      freeChatLimit !== originalSettings.freeChatLimit
    );
  }, [requireBothMembersHavePlan, allChatsFree, freeChatLimit, originalSettings]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Please login to continue');
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.get(`${API_URL}/admin/chat-settings`);

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setRequireBothMembersHavePlan(data.requireBothMembersHavePlan === true);
        setAllChatsFree(data.allChatsFree === true);
        setFreeChatLimit(data.freeChatLimit || 0);
        
        setOriginalSettings({
          requireBothMembersHavePlan: data.requireBothMembersHavePlan === true,
          allChatsFree: data.allChatsFree === true,
          freeChatLimit: data.freeChatLimit || 0
        });
      }
    } catch (error: any) {
      console.error('Error fetching chat settings:', error);
      toast.error('Failed to load chat settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Please login to continue');
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.put(`${API_URL}/admin/chat-settings`, {
        requireBothMembersHavePlan,
        allChatsFree,
        freeChatLimit
      });

      if (response.data.success) {
        toast.success('Chat settings saved successfully');
        setOriginalSettings({
          requireBothMembersHavePlan,
          allChatsFree,
          freeChatLimit
        });
        setHasChanges(false);
      }
    } catch (error: any) {
      console.error('Error saving chat settings:', error);
      toast.error(error.response?.data?.message || 'Failed to update chat settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setRequireBothMembersHavePlan(originalSettings.requireBothMembersHavePlan);
    setAllChatsFree(originalSettings.allChatsFree);
    setFreeChatLimit(originalSettings.freeChatLimit);
    setHasChanges(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold">Chat Permission Settings</h2>
        <p className="text-gray-600 mt-1">
          Configure chat permissions and subscription requirements
        </p>
        {/* Current Mode Badge */}
        <div className="mt-3 flex flex-wrap gap-2">
          {allChatsFree ? (
            <Badge className="bg-rose-600">Current Mode: All Chats Free (Global)</Badge>
          ) : (
            <>
              <Badge className={requireBothMembersHavePlan ? 'bg-green-600' : 'bg-blue-600'}>
                Mode: {requireBothMembersHavePlan ? 'Strict (Both Need Plan)' : 'Flexible (Initiator Needs Plan)'}
              </Badge>
              {freeChatLimit > 0 && (
                <Badge className="bg-amber-600">Limit: {freeChatLimit} Free Chats/User</Badge>
              )}
            </>
          )}
        </div>
      </div>

      {/* Chat Permission Toggle */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-rose-600" />
            Chat Subscription Requirements
          </CardTitle>
          <CardDescription>
            Control who can chat based on subscription status
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Free Chat Limit Setting */}
          <div className="space-y-4">
            <div className="flex items-start justify-between p-4 border rounded-lg bg-white">
              <div className="flex-1 space-y-2 pr-4">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-rose-600" />
                  <span className="text-base font-medium">Allow Free Chats for Free Users</span>
                </div>
                <p className="text-sm text-gray-600">
                  Allow users without a subscription plan to start a limited number of chats for free.
                </p>
              </div>
              <Switch
                checked={freeChatLimit > 0}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setFreeChatLimit(1); // Default to 1 if enabled
                  } else {
                    setFreeChatLimit(0); // Set to 0 if disabled
                  }
                }}
                disabled={saving}
              />
            </div>

            {freeChatLimit > 0 && (
              <div className="flex items-start justify-between p-4 border rounded-lg bg-gray-50 ml-6 animate-in slide-in-from-top-1">
                <div className="flex-1 space-y-1 pr-4">
                  <span className="text-sm font-medium">Number of Free Chats</span>
                  <p className="text-xs text-gray-500">
                    Max number of unique participants a free user can initiate a chat with.
                  </p>
                </div>
                <div className="w-24">
                  <Input
                    type="number"
                    min="1"
                    value={freeChatLimit}
                    onChange={(e) => setFreeChatLimit(Math.max(1, parseInt(e.target.value) || 1))}
                    disabled={saving}
                    className="text-right"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex items-start justify-between p-4 border rounded-lg bg-rose-50 border-rose-100">
            <div className="flex-1 space-y-2 pr-4">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-rose-600" />
                <span className="text-base font-medium text-rose-900">Make All Chats Free (Global Override)</span>
              </div>
              <p className="text-sm text-rose-700">
                When enabled, ANY user can chat regardless of plans or limits. This overrides the free chat limit above.
              </p>
            </div>
            <Switch
              checked={allChatsFree}
              onCheckedChange={setAllChatsFree}
              disabled={saving}
            />
          </div>

          {/* Strict Mode Toggle */}
          <div className="flex items-start justify-between p-4 border rounded-lg bg-white">
            <div className="flex-1 space-y-2 pr-4">
              <div className="flex items-center gap-2">
                <Crown className="h-4 w-4 text-amber-600" />
                <span className="text-base font-medium">Require Both Members to Have Active Plan</span>
              </div>
              <p className="text-sm text-gray-600">
                When enabled, both members must have an active subscription to chat with each other
              </p>
            </div>
            <Switch
              checked={requireBothMembersHavePlan}
              onCheckedChange={setRequireBothMembersHavePlan}
              disabled={saving}
            />
          </div>

          {/* Explanation Cards moved to bottom if needed or removed to keep it clean */}

          {/* Explanation Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            {/* When Toggle is ON */}
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${
                requireBothMembersHavePlan 
                  ? 'border-green-500 bg-green-50 ring-2 ring-green-500' 
                  : 'border-gray-200 hover:border-green-300'
              }`}
              onClick={() => setRequireBothMembersHavePlan(true)}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${requireBothMembersHavePlan ? 'bg-green-500' : 'bg-gray-300'}`} />
                  When ENABLED (Strict Mode)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <Users className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-green-900">Both Must Have Plan</p>
                    <p className="text-green-700 text-xs">Member A (Premium) + Member B (Premium) = ✅ Can Chat</p>
                    <p className="text-red-700 text-xs mt-1">Member A (Premium) + Member B (Free) = ❌ Cannot Chat</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* When Toggle is OFF */}
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${
                !requireBothMembersHavePlan 
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500' 
                  : 'border-gray-200 hover:border-blue-300'
              }`}
              onClick={() => setRequireBothMembersHavePlan(false)}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${!requireBothMembersHavePlan ? 'bg-blue-500' : 'bg-gray-300'}`} />
                  When DISABLED (Flexible Mode)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <Crown className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-blue-900">Initiator Must Have Plan</p>
                    <p className="text-blue-700 text-xs">Member A (Premium) starts chat with Member B (Free) = ✅ Both Can Chat</p>
                    <p className="text-red-700 text-xs mt-1">Member A (Free) tries to chat = ❌ Cannot Initiate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info Box */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900 space-y-2">
                  <p className="font-medium">Understanding Chat Permissions:</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-800 ml-2">
                    <li><strong>Strict Mode (ON):</strong> Ensures both parties are paying subscribers. Good for premium-only features.</li>
                    <li><strong>Flexible Mode (OFF):</strong> Allows premium members to chat with free members. Good for user engagement and conversions.</li>
                    <li>Admins can always chat regardless of this setting</li>
                    <li>Changes take effect immediately for all new messages</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons - Always Visible */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="flex items-center gap-2">
              {hasChanges && (
                <>
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  <span className="font-medium text-amber-900">You have unsaved changes</span>
                </>
              )}
            </div>
            <div className="flex gap-3">
              {hasChanges && (
                <Button variant="outline" onClick={handleReset} disabled={saving}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reset
                </Button>
              )}
              <Button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="bg-rose-600 hover:bg-rose-700"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}